import React, { useContext, useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  InputBase,
} from '@mui/material';
import { Menu as MenuIcon, Logout, Person } from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// Your logo
const LOGO_URL = 'https://dispatchv2.lalivraison.ma/logolaliv.png';

// Custom styled components with #21ccbb
const LuxeAppBar = styled(AppBar)(() => ({
  zIndex: 1300,
  background: '#FFFFFF', // White background as in the image
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Subtle shadow
  position: 'fixed', // Already fixed, ensuring it stays at the top
  top: 0, // Explicitly set to the top
  left: 0,
  right: 0,
}));

const SearchBar = styled(Box)(() => ({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'center',
  padding: '0 16px',
}));

const SearchInput = styled(InputBase)(() => ({
  background: '#F5F5F5',
  borderRadius: '8px',
  padding: '8px 16px',
  width: '50%',
  maxWidth: '600px',
  fontSize: '1rem',
  color: '#666',
  '& .MuiInputBase-input::placeholder': {
    color: '#999',
  },
}));

const IconButtonStyled = styled(IconButton)(() => ({
  color: '#21ccbb',
  background: 'rgba(33, 204, 187, 0.1)',
  marginLeft: '8px',
  '&:hover': {
    background: '#21ccbb',
    color: '#FFFFFF',
  },
}));

const TopBar = () => {
  const { user, logout } = useContext(AuthContext);
  const { toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  // Profile menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  return (
    <LuxeAppBar position="fixed">
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px' }}>
        {/* Left Section: Menu Button & Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Toggle Sidebar">
            <motion.div
              whileHover={{ scale: 1.3 }}
              whileTap={{ scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <IconButtonStyled onClick={toggleSidebar} aria-label="toggle sidebar">
                <MenuIcon sx={{ fontSize: '1.6rem' }} />
              </IconButtonStyled>
            </motion.div>
          </Tooltip>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <img
              src={LOGO_URL}
              alt="La Livraison Logo"
              style={{ height: '40px', display: { xs: 'none', sm: 'block' } }}
            />
          </motion.div>
        </Box>

        {/* Center Section: Search Bar */}
        <SearchBar>
          <SearchInput
            placeholder="Search"
            startAdornment={
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#21ccbb"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </Box>
            }
          />
        </SearchBar>

        {/* Right Section: Profile Icon Only */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Profile">
            <IconButtonStyled onClick={handleMenuOpen}>
              <Person sx={{ fontSize: '1.6rem' }} />
            </IconButtonStyled>
          </Tooltip>

          {/* Profile Menu */}
          {open && (
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'profile-button',
              }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1,
                  borderRadius: '8px',
                  minWidth: '150px',
                  background: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  '& .MuiMenuItem-root': {
                    padding: '10px 16px',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: '#21ccbb',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    '&:hover': {
                      background: 'transparent',
                      color: '#1aa89b',
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleProfileClick}>
                <Person sx={{ fontSize: '1.2rem', color: '#21ccbb' }} />
                Profile
              </MenuItem>
              <Divider sx={{ my: 0.5, borderColor: '#21ccbb10' }} />
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ fontSize: '1.2rem', color: '#21ccbb' }} />
                Logout
              </MenuItem>
            </Menu>
          )}
        </Box>
      </Toolbar>
    </LuxeAppBar>
  );
};

export default TopBar;